import { create } from 'zustand'
import api from '../lib/api'

const useJourneyStore = create((set, get) => ({
  // State
  currentSession: null,
  messages: [],
  isLoading: false,
  currentPhase: null,
  anchors: {
    truth: null,
    expression: null,
    attention: null,
    evolution: null,
    pitchDeck: null
  },
  journeyProgress: 0,
  
  // Actions
  startJourney: async () => {
    set({ isLoading: true })
    try {
      console.log('🚀 Starting journey with API call to:', api.defaults.baseURL + '/journey/start')
      
      const response = await api.post('/journey/start', {
        brand_name: "New Brand",
        company_name: "New Company"
      })
      const data = response.data
      
      console.log('📥 Journey start response:', data)
      
      // Handle the response format from backend
      if (data.session_id && data.message) {
        const welcomeMessage = {
          id: Date.now(),
          role: 'assistant', 
          content: data.message, 
          timestamp: data.timestamp || new Date().toISOString(),
          phase: data.phase || 'conversation_initiation',
          agent: data.agent_name || 'Samvadic Master'
        }
        
        set({
          currentSession: data.session_id,
          messages: [welcomeMessage],
          currentPhase: data.phase || 'conversation_initiation',
          isLoading: false
        })
        
        return { success: true }
      } else {
        console.error('❌ Invalid start response:', data)
        set({ isLoading: false })
        return { 
          success: false, 
          error: data.detail || 'Failed to start journey' 
        }
      }
    } catch (error) {
      set({ isLoading: false })
      console.error('Journey start error:', error)
      
      return { 
        success: false, 
        error: error.response?.data?.detail || error.message || 'Failed to start journey' 
      }
    }
  },

  resumeJourney: async () => {
    set({ isLoading: true })
    try {
      console.log('🔄 Resuming journey with API call to:', api.defaults.baseURL + '/journey/resume')
      
      const response = await api.post('/journey/resume')
      const data = response.data
      
      console.log('📥 Journey resume response:', data)
      
      if (data.session_id && data.message) {
        const resumeMessage = { 
          id: Date.now(),
          role: 'assistant', 
          content: data.message, 
          timestamp: data.timestamp || new Date().toISOString(),
          phase: data.phase,
          agent: data.agent_name || 'Samvadic Master'
        }
        
        // Load existing conversation history first
        await get().loadConversationHistory()
        
        // Add the resume message
        set(state => ({
          messages: [...state.messages, resumeMessage],
          currentSession: data.session_id,
          currentPhase: data.phase,
          isLoading: false
        }))
        
        return { success: true }
      } else {
        console.error('❌ Invalid resume response:', data)
        set({ isLoading: false })
        return { 
          success: false, 
          error: data.detail || 'Failed to resume journey' 
        }
      }
    } catch (error) {
      set({ isLoading: false })
      console.error('Journey resume error:', error)
      
      return { 
        success: false, 
        error: error.response?.data?.detail || error.message || 'Failed to resume journey' 
      }
    }
  },
  
  sendMessage: async (message) => {
    const { currentSession, messages } = get()
    
    if (!currentSession) {
      return {
        success: false,
        error: 'No active journey session. Please start a new journey.'
      }
    }
    
    // Add user message immediately
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    }
    
    set({ 
      messages: [...messages, userMessage],
      isLoading: true 
    })
    
    try {
      console.log('💬 Sending message to:', api.defaults.baseURL + '/journey/continue')
      
      const response = await api.post('/journey/continue', { 
        message
      })
      const data = response.data
      
      console.log('📥 Continue response:', data)
      
      if (data.session_id && data.message) {
        // Add AI response
        const assistantMessage = {
          id: Date.now() + 1,
          role: 'assistant',
          content: data.message,
          timestamp: data.timestamp || new Date().toISOString(),
          phase: data.phase,
          agent: data.agent_name || 'Samvadic'
        }
        
        set(state => ({
          messages: [...state.messages, assistantMessage],
          currentPhase: data.phase,
          isLoading: false
        }))
        
        // Update anchors if any were completed
        if (data.metadata && data.metadata.completed_anchors) {
          get().updateAnchors(data.metadata.completed_anchors)
        }
        
        return { success: true }
      } else {
        set({ isLoading: false })
        return { 
          success: false, 
          error: data.detail || 'Failed to send message' 
        }
      }
    } catch (error) {
      set({ isLoading: false })
      console.error('Send message error:', error)
      
      return { 
        success: false, 
        error: error.response?.data?.detail || error.message || 'Failed to send message' 
      }
    }
  },
  
  loadJourneyStatus: async () => {
    try {
      console.log('📊 Loading journey status from:', api.defaults.baseURL + '/journey/status')
      
      const response = await api.get('/journey/status')
      const data = response.data
      
      console.log('📥 Status response:', data)
      
      if (data.session_id) {
        set({
          currentSession: data.session_id,
          currentPhase: data.current_phase,
          journeyProgress: data.completion_percentage || 0
        })
        
        return { success: true }
      } else {
        return { 
          success: false, 
          error: data.detail || 'Failed to load journey status' 
        }
      }
    } catch (error) {
      console.error('Load journey status error:', error)
      
      // If journey status endpoint doesn't exist, it's okay - user hasn't started yet
      if (error.response?.status === 404) {
        console.log('ℹ️ No existing journey found - this is normal for new users')
        return { success: true }
      }
      
      return { 
        success: false, 
        error: error.response?.data?.detail || error.message || 'Failed to load journey status' 
      }
    }
  },

  loadConversationHistory: async () => {
    try {
      console.log('💬 Loading conversation history from:', api.defaults.baseURL + '/journey/history')
      
      const response = await api.get('/journey/history')
      const data = response.data
      
      console.log('📥 History response:', data)
      
      if (data.messages && Array.isArray(data.messages)) {
        const formattedMessages = data.messages.map((msg, index) => ({
          id: msg.id || index,
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp,
          phase: msg.phase || data.session_metadata?.current_phase,
          agent: msg.agent_name || 'Samvadic'
        }))
        
        set({
          messages: formattedMessages,
          currentSession: data.session_id,
          currentPhase: data.session_metadata?.current_phase,
          journeyProgress: data.session_metadata?.journey_progress || 0
        })
        
        console.log('✅ Loaded', formattedMessages.length, 'messages into store')
        return { 
          success: true, 
          messages: formattedMessages,
          count: formattedMessages.length 
        }
      }
      
      console.log('📭 No messages found in history response')
      return { success: true, messages: [], count: 0 }
    } catch (error) {
      console.error('Load conversation history error:', error)
      
      if (error.response?.status === 404) {
        console.log('ℹ️ No conversation history found')
        return { success: true, messages: [], count: 0 }
      }
      
      return { 
        success: false, 
        error: error.response?.data?.detail || error.message || 'Failed to load conversation history' 
      }
    }
  },
  
  updateAnchors: (anchorData) => {
    console.log('📝 Updating anchors:', anchorData)
    set(state => ({
      anchors: {
        ...state.anchors,
        ...anchorData
      },
      journeyProgress: _calculateProgress({
        ...state.anchors,
        ...anchorData
      })
    }))
  },
  
  clearJourney: () => {
    console.log('🧹 Clearing journey data')
    set({
      currentSession: null,
      messages: [],
      currentPhase: null,
      anchors: {
        truth: null,
        expression: null,
        attention: null,
        evolution: null,
        pitchDeck: null
      },
      journeyProgress: 0,
      isLoading: false
    })
  },
  
  // Consultant mode
  askConsultant: async (question) => {
    set({ isLoading: true })
    try {
      console.log('💭 Asking consultant:', question)
      
      const response = await api.post('/consult', { query: question })
      const data = response.data
      
      console.log('📥 Consultant response:', data)
      
      set({ isLoading: false })
      
      if (data.consultation) {
        return { 
          success: true, 
          response: data.consultation, 
          confidence: data.confidence 
        }
      } else {
        return { 
          success: false, 
          error: data.detail || 'Consultant query failed' 
        }
      }
    } catch (error) {
      set({ isLoading: false })
      console.error('Consultant query error:', error)
      return { 
        success: false, 
        error: error.response?.data?.detail || error.message || 'Consultant query failed' 
      }
    }
  },

  // Debug function to test API connectivity
  testAPIConnection: async () => {
    try {
      console.log('🔧 Testing API connection...')
      console.log('Base URL:', api.defaults.baseURL)
      console.log('Full health URL:', api.defaults.baseURL.replace('/api', '') + '/health')
      
      // Test health endpoint (remove /api prefix for health)
      const healthResponse = await fetch(api.defaults.baseURL.replace('/api', '') + '/health')
      const healthData = await healthResponse.json()
      
      console.log('✅ Health check response:', healthData)
      return { success: true, health: healthData }
      
    } catch (error) {
      console.error('❌ API connection test failed:', error)
      return { success: false, error: error.message }
    }
  },

  // Debug function to check current state
  debugState: () => {
    const state = get()
    console.log('🐛 Current Journey Store State:', {
      currentSession: state.currentSession,
      messagesCount: state.messages.length,
      currentPhase: state.currentPhase,
      isLoading: state.isLoading,
      journeyProgress: state.journeyProgress
    })
    return state
  }
}))

// Helper function to calculate journey progress
function _calculateProgress(anchors) {
  const totalAnchors = 5 // truth, expression, attention, evolution, pitchDeck
  const completedAnchors = Object.values(anchors).filter(anchor => anchor !== null).length
  return Math.round((completedAnchors / totalAnchors) * 100)
}

export { useJourneyStore }